import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import {
  GameTable,
  CreateGameTableRequest,
  UpdateGameTableRequest,
} from "../../api/services/gameTables/type";

import PrimaryButton from "../ui/PrimaryButton";
import CustomInput from "../ui/CustomInput";

interface GameTableFormProps {
  table?: GameTable;
  isLoading?: boolean;
  onSubmit: (data: CreateGameTableRequest | UpdateGameTableRequest) => void;
  onCancel: () => void;
  games?: { id: string; name: string }[];
}

type FormData = {
  name: string;
  min_amount: number;
  max_amount: number;
  is_limit: boolean;
  game_id: string;
  limit_number: number;
};

export const GameTableForm: React.FC<GameTableFormProps> = ({
  table,
  isLoading = false,
  onSubmit,
  onCancel,
  games = [],
}) => {
  const isEditMode = !!table;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: table?.name || "",
      min_amount: table?.minAmount || 0,
      max_amount: table?.maxAmount || 0,
      is_limit: table?.isLimit || false,
      game_id: table?.gameId || (games[0]?.id ?? ""),
      limit_number: table?.limitNumber || 0,
    },
  });

  const minAmount = watch("min_amount");
  const isLimit = watch("is_limit");

  const shouldShowLimit = useMemo(() => {
    if (isEditMode && table?.isLimit) {
      return true;
    }
    return isLimit;
  }, [isEditMode, table?.isLimit, isLimit]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">
            {isEditMode ? "Edit Game Table" : "Create Game Table"}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4"
          autoComplete="off"
        >
          <CustomInput
            label="Table Name"
            placeholder="Enter table name"
            type="text"
            name="name"
            register={register}
            validation={{
              required: "Table name is required",
              minLength: { value: 2, message: "At least 2 characters" },
            }}
            error={errors.name}
            disabled={isLoading}
          />
          <CustomInput
            label="Min Amount"
            placeholder="Minimum amount"
            type="number"
            name="min_amount"
            register={register}
            validation={{
              required: "Min amount is required",
              valueAsNumber: true,
              min: { value: 1, message: "Min amount must be greater than 0" },
              validate: (v: string | number | boolean) => {
                const n = Number(v);
                if (Number.isNaN(n)) return "Invalid min amount";
                if (n <= 0) return "Min amount must be greater than 0";
                return true;
              },
            }}
            error={errors.min_amount}
            disabled={isLoading}
          />
          <CustomInput
            label="Max Amount"
            placeholder="Maximum amount"
            type="number"
            name="max_amount"
            register={register}
            validation={{
              required: "Max amount is required",
              validate: (v: string | number | boolean) => {
                const max = Number(v);
                const min = Number(minAmount ?? 0);
                if (Number.isNaN(max)) return "Invalid max amount";
                if (max <= min)
                  return "Max amount must be greater than Min amount";
                return true;
              },
            }}
            error={errors.max_amount}
            disabled={isLoading}
          />
          <div>
            <label className="block text-slate-300 mb-1">Is Limit</label>
            <select
              {...register("is_limit", {
                setValueAs: (value) => value === "true",
              })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              disabled={isLoading}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
          {shouldShowLimit && (
            <CustomInput
              label="Limit Number"
              placeholder="Limit number"
              type="number"
              name="limit_number"
              register={register}
              validation={{ required: "Limit number is required", min: 0 }}
              error={errors.limit_number}
              disabled={isLoading}
            />
          )}
          <div>
            <label className="block text-slate-300 mb-1">Game</label>
            <select
              {...register("game_id", { required: "Game is required" })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              disabled={isLoading}
            >
              {games.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            {errors.game_id && (
              <span className="text-red-400 text-xs">
                {errors.game_id.message}
              </span>
            )}
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : isEditMode ? "Update" : "Create"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};
